import {
  AlertComponent,
  BasicInfoComponent,
  CombinedChart,
  FormSection,
  InputExcelFile,
  NavBar,
  PdfProposalFile,
  ProposalDescription,
  SolutionComponent,
  TallyTable,
  VSvsTVDChart,
  WBDDesign,
  Wellbore3d,
} from "../../components";
import { useFetch, usePermissions } from "../../hooks";
import { ClientProps, ProductProps } from "../../interfaces/interfaces";
import styles from "../main.module.sass";
import { useContext, useEffect, useRef, useState } from "react";
import DataContext from "../../context/DataContext";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { PDFView } from "../../components/pdf";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useScreenshot } from "usescreenshot-react";
import SellIcon from "@mui/icons-material/Sell";
import SimulatorContext from "../../context/SimulatorContext";
import { initial_prod_chart } from "../../utils/data";
import { useDate } from "../../hooks/useDate";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import ScienceIcon from "@mui/icons-material/Science";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import BuildIcon from "@mui/icons-material/Build";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

interface TechProposalInfoProps {
  clients: ClientProps[];
  products: ProductProps[];
  customTools: ProductProps[];
}

interface FetchResponse {
  success?: boolean;
  data: TechProposalInfoProps;
}

export const TechDesignForm = () => {
  const { idTech } = useParams();
  let [searchParams] = useSearchParams();
  const { getDateFromString } = useDate();
  const pdf = searchParams.get("pdf");
  const { takeScreenshot } = useScreenshot();
  const ref = useRef<HTMLDivElement>(null);
  const { data } = useFetch<FetchResponse>("tech-proposal");
  const [inEdit, setInEdit] = useState(false);
  const [inPdf, setInPdf] = useState(false);
  const [isThereProdImage, setIsThereProdImage] = useState(false);
  const navigate = useNavigate();
  const { canManageDesigns } = usePermissions();
  const [products, setProducts] = useState<ProductProps[]>([]);
  const [customTools, setCustomTools] = useState<ProductProps[]>([]);

  const {
    fetchDataClient,
    toSave,
    onSaveProp,
    onEditprop,
    onUpdateBasicInfo,
    onCancelEdition,
    onCreatePdf,
    fetchDataTechProp,
    fetchDataProducts,
    onResetValues,
    isSuccess,
    isError,
    data: dataProposal,
  } = useContext(DataContext);

  const { fetchTechSolution } = useContext(SimulatorContext);

  useEffect(() => {
    fetchDataClient();
    if (idTech && idTech !== "0") {
      fetchDataTechProp(parseInt(idTech));
      fetchTechSolution(parseInt(idTech));
      setInEdit(true);
      dataProposal.basicInfo.prodImage && setIsThereProdImage(true);
      if (pdf) setInPdf(true);
    } else {
      setInEdit(false);
      setInPdf(false);
      onResetValues();
    }

    return () => {};
  }, [idTech]);

  useEffect(() => {
    fetchDataProducts();
  }, []);

  useEffect(() => {
    if (data) {
      const products = data ? data.data.products : [];
      const customTools = data ? data.data.customTools : [];
      setProducts(products);
      setCustomTools(customTools);
    }
  }, [data]);

  const buttons = [
    {
      title: "Cancel",
      action: onCancelEdition,
      icon: <CancelIcon />,
      disabled: !!inPdf,
    },
    ...(canManageDesigns
      ? [
          {
            title: "Order",
            action: () => navigate(`/sales/tech/${idTech}`),
            icon: <SellIcon />,
            disabled: !toSave,
          },
          {
            title: "Save",
            action: inEdit ? () => idTech && onEditprop(idTech) : onSaveProp,
            icon: <SaveIcon />,
            disabled: !toSave,
          },
        ]
      : []),
    {
      title: !inPdf ? "To Pdf" : "Go Back",
      action: () => {
        onCreatePdf();
        setInPdf(!inPdf);
      },
      icon: !inPdf ? <PictureAsPdfIcon /> : <ArrowBackIosIcon />,
    },
  ];

  const handleReadExcelFile = (data: any) => {
    let labels = [];
    let dataset1 = [],
      dataset2 = [],
      dataset3 = [];
    for (let i = 1; i < data.length; i++) {
      labels.push(getDateFromString(data[i][0]));
      dataset1.push(data[i][1]);
      dataset2.push(data[i][2]);
      dataset3.push(data[i][3]);
    }
    setIsThereProdImage(true);
    onUpdateBasicInfo("prodChartData", {
      labels,
      dataset1,
      dataset2,
      dataset3,
    });
  };

  const handleRemoveImage = () => {
    onUpdateBasicInfo("prodChartData", initial_prod_chart);
    onUpdateBasicInfo("prodImage", "");
    setIsThereProdImage(false);
  };

  const getImage = () => {
    if (!ref.current) {
      return;
    }
    takeScreenshot(ref.current, {
      backgroundColor: null,
      logging: false,
    })
      .then((res) => onUpdateBasicInfo("prodImage", res))
      .catch(console.log);
  };

  return (
    <>
      <NavBar title="Technical Proposal" buttons={buttons} />
      <div className={styles.techProposalForm}>
        {inPdf ? (
          <PDFView
            fileName={dataProposal.basicInfo.customName}
            document={(us) => (
              <PdfProposalFile data={dataProposal} unitSystem={us} />
            )}
            action={pdf ? onCreatePdf : () => {}}
          />
        ) : (
          <>
            <FormSection title="Well Information" icon={<InfoOutlinedIcon />}>
              <BasicInfoComponent />
            </FormSection>

            <FormSection
              title="Vertical Section vs True Vertical Depth"
              icon={<ShowChartIcon />}
              defaultExpanded={false}
            >
              <VSvsTVDChart />
            </FormSection>

            <FormSection title="Production History" icon={<BarChartIcon />}>
              <InputExcelFile
                onReadExcelData={handleReadExcelFile}
                onGetImage={getImage}
                disabled={!isThereProdImage}
                onRemoveImage={handleRemoveImage}
              />
              <div ref={ref} className={styles.center}>
                <CombinedChart
                  prodChartData={dataProposal.basicInfo.prodChartData}
                />
              </div>
            </FormSection>

            <FormSection title="Simulators" icon={<ScienceIcon />}>
              <SolutionComponent />
            </FormSection>

            <FormSection
              title="3D Wellbore"
              icon={<ViewInArIcon />}
              defaultExpanded={false}
            >
              <Wellbore3d />
            </FormSection>

            <FormSection
              title="Tool Description"
              icon={<BuildIcon />}
              defaultExpanded={false}
            >
              <ProposalDescription />
            </FormSection>

            <FormSection title="Tally Design" icon={<ListAltIcon />}>
              <TallyTable products={products} customTools={customTools} />
            </FormSection>

            <FormSection title="Wellbore Design" icon={<AccountTreeIcon />}>
              <WBDDesign />
            </FormSection>
          </>
        )}
        {isSuccess && <AlertComponent type="success" />}
        {isError && <AlertComponent type="error" />}
      </div>
    </>
  );
};
